import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  albumsControllerCreate,
  AlbumsControllerCreate$Params,
  albumsControllerDelete,
  albumsControllerDeletePhoto,
  albumsControllerFindAllAdmin,
  AlbumsControllerFindAllAdmin$Params,
  albumsControllerFindOne,
  albumsControllerSetTitlePhoto,
  albumsControllerUpdate,
  AlbumsControllerUpdate$Params,
  albumsControllerUploadPhotos,
  AlbumsControllerUploadPhotos$Params,
} from '@klubovna/api-client';
import { AlbumResponse, CreateAlbumDto, UpdateAlbumDto } from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface AlbumsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'name.sk' | 'name.en' | 'createdAt' | 'updatedAt' | 'photoCount' | 'isActive' | 'eventDate';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class AlbumsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: AlbumsQueryParams): Observable<PaginatedResponse<AlbumResponse>> {
    const apiParams: AlbumsControllerFindAllAdmin$Params = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      isActive: params?.isActive,
    };

    return albumsControllerFindAllAdmin(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(
        map(response => toPaginatedResponse<AlbumResponse>(response.body)),
      );
  }

  findOne(id: string): Observable<AlbumResponse> {
    return albumsControllerFindOne(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as AlbumResponse));
  }

  create(albumData: CreateAlbumDto): Observable<AlbumResponse> {
    const params: AlbumsControllerCreate$Params = {
      body: albumData,
    };
    return albumsControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as AlbumResponse));
  }

  update(id: string, albumData: UpdateAlbumDto): Observable<AlbumResponse> {
    const params: AlbumsControllerUpdate$Params = {
      id,
      body: albumData,
    };
    return albumsControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as AlbumResponse));
  }

  delete(id: string): Observable<void> {
    return albumsControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  uploadPhotos(id: string, files: File[]): Observable<AlbumResponse> {
    const params: AlbumsControllerUploadPhotos$Params = {
      id,
      body: { files },
    };
    return albumsControllerUploadPhotos(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as AlbumResponse));
  }

  uploadPhoto(id: string, file: File): Observable<AlbumResponse> {
    const params: AlbumsControllerUploadPhotos$Params = {
      id,
      body: { files: [ file ] },
    };
    return albumsControllerUploadPhotos(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as AlbumResponse));
  }

  deletePhoto(id: string, photoId: string): Observable<AlbumResponse> {
    return albumsControllerDeletePhoto(this.http, this.apiConfig.rootUrl, { id, photoId })
      .pipe(map(response => response.body as AlbumResponse));
  }

  setTitlePhoto(id: string, photoId: string): Observable<AlbumResponse> {
    return albumsControllerSetTitlePhoto(this.http, this.apiConfig.rootUrl, { id, photoId })
      .pipe(map(response => response.body as AlbumResponse));
  }
}
